import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';

export interface FormDialogProps {
  open: boolean;
  /** Verb + object: "Create site", "Invite user". */
  title: string;
  /** The verb again: "Create", "Invite". */
  submitLabel: string;
  /** Runs the change. Throwing keeps the dialog open with the error shown inside it. */
  onSubmit: () => Promise<void>;
  onClose: () => void;
  /** False while the form is incomplete or invalid. */
  canSubmit?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md';
  /** Use the destructive colour for the submit button. */
  destructive?: boolean;
  /** Turns a failure into the message shown in the dialog (default: its message). */
  describeError?: (err: unknown) => string;
  submitTestId?: string;
  cancelTestId?: string;
  testId?: string;
  children?: React.ReactNode;
}

/**
 * The one modal for changes (web-frontend.md, Modals): one task, verb + object
 * title, server errors shown inside the dialog so the user keeps their input,
 * no dismissing while submitting, full screen on phones.
 */
export function FormDialog({
  open,
  title,
  submitLabel,
  onSubmit,
  onClose,
  canSubmit = true,
  maxWidth = 'sm',
  destructive = false,
  describeError,
  submitTestId,
  cancelTestId,
  testId,
  children,
}: FormDialogProps) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
  };
  const submit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (busy || !canSubmit) return;
    setBusy(true);
    setError(null);
    onSubmit()
      .catch((err: unknown) => {
        setError(
          describeError ? describeError(err) : err instanceof Error ? err.message : String(err),
        );
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={phone}
      fullWidth
      maxWidth={maxWidth}
      data-testid={testId}
      slotProps={{ paper: { component: 'form', onSubmit: submit } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" data-testid="dialog-error">
              {error}
            </Alert>
          )}
          {children}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy} data-testid={cancelTestId}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy || !canSubmit}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          data-testid={submitTestId}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
